import { IconName, Intent, MaybeElement } from "@blueprintjs/core";
import { QueryClient } from "@tanstack/react-query";
import { useOpenComponent } from "app/jm";
import { ZodSchema, z } from "zod";
import type { ReactElement, ReactNode } from "react";
import type {
  Permission,
  PermissionConditionalPolicyInputTypes,
} from "domain/application/permissions/Permissions";
import type { PermissionCheckInput as GraphqlPermissionCheckInput } from "src/gql/graphql";

export type PermissionCheckInput<P extends Permission = Permission> =
  P extends keyof PermissionConditionalPolicyInputTypes
    ? {
        permission: P;
        conditions?: PermissionConditionalPolicyInputTypes[P];
      }
    : {
        permission: P;
        conditions?: never;
      };

export const serializePermissionChecks = (
  permissions: PermissionCheckInput[],
): GraphqlPermissionCheckInput[] =>
  permissions.map(({ permission, conditions }) =>
    conditions === undefined
      ? { permission }
      : { permission, conditions: JSON.stringify(conditions) },
  );

export type ActionPermissionCheck = (
  | PermissionCheckInput
  | { permission?: never; conditions?: never }
) & {
  and?: ActionPermissionCheck[];
  or?: ActionPermissionCheck[];
};

export type ActionPermissions = ActionPermissionCheck | ActionPermissionCheck[];

/** Each entry preserves the result of one declared permission check. */
export type ActionPermissionResults = Partial<Record<Permission, boolean>>[];

export type RecordId = string;

export type ActionScope = "global" | "record";

export type ActionType = "create" | "read" | "update" | "delete";

export type Invoke<
  ParamsSchema extends ZodSchema | undefined,
  PreparedData = any,
  SuccessSchema extends ZodSchema | undefined = undefined,
> = (props: {
  openComponent: ReturnType<typeof useOpenComponent>;
  openOverlay: (fn: (params: { close: () => void }) => ReactNode) => void;
  setLoading: (value: boolean) => void;
  success: (args?: {
    params?: SuccessSchema extends ZodSchema ? z.infer<SuccessSchema> : void;
    successMsg?: string;
    silent?: boolean;
    refresh?: boolean;
    close?: boolean;
  }) => void;
  cancel: () => void;
  fail: (args: { error?: Error; errorMsg?: string; silent?: boolean }) => void;
  queryClient: QueryClient;
  records: PreparedData[];
  permissions?: ActionPermissionResults;
  params: ParamsSchema extends ZodSchema ? z.infer<ParamsSchema> : undefined;
}) => void | Promise<void>;

export type ActionPropParams<
  ParamsSchema extends ZodSchema | undefined,
  PreparedData = any,
> = {
  records: PreparedData[];
  permissions?: ActionPermissionResults;
  params: ParamsSchema extends ZodSchema ? z.infer<ParamsSchema> : undefined;
};

export type ActionConfig<
  ParamsSchema extends ZodSchema | undefined = undefined,
  InputData = any,
  PreparedData = InputData,
  SuccessSchema extends ZodSchema | undefined = undefined,
> = {
  id?: string;
  recordType?: string;
  name:
    | string
    | ((
        params: ActionPropParams<ParamsSchema, PreparedData>,
      ) => string | Promise<string>);
  intent?: Intent;
  icon?: IconName | MaybeElement;
  disabled?: boolean;
  //isVisible?: boolean;
  onInvoke: Invoke<ParamsSchema, PreparedData, SuccessSchema>;
  prepare?: (inputData: InputData) => PreparedData;
  canInvoke?: (
    params: ActionPropParams<ParamsSchema, PreparedData>,
  ) => boolean | Promise<boolean>;
  // Note: We experimented with a getInvokeData method, but the issue is
  // that in Typescript we can't infer InvokeData based off the already inferred
  // PreparedData (inferred from prepare()). We can only infer "one layer" deep.
  // getInvokeData: (params: { records: PreparedData[] }) => InvokeData;
  hideOnNotInvokable?:
    | boolean
    | ((
        params: ActionPropParams<ParamsSchema, PreparedData>,
      ) => boolean | Promise<boolean>);
  paramsSchema?: ParamsSchema;
  successSchema?: SuccessSchema;
  tooltip?:
    | ReactNode
    | ((
        params: ActionPropParams<ParamsSchema, PreparedData>,
      ) => Promise<ReactNode> | ReactNode);
  order?: number;
  permissions?:
    | ActionPermissions
    | ((
        params: Omit<
          ActionPropParams<ParamsSchema, PreparedData>,
          "permissions"
        >,
      ) => ActionPermissions);
  scope?: ActionScope;
  actionType?: ActionType;
  origin?: "static" | "webhook" | "linked";
  favouriteId?: string;
  /** The action prepares records generically, without a domain-specific registry entry. */
  acceptsAnyRecordType?: boolean;
  canInvokeViaUrl?: boolean;
};

/**
 * An Action that doesn't have any parameters bound to it
 */
export type UnboundAction<ParamsSchema extends ZodSchema> = Omit<
  ActionConfig<ParamsSchema>,
  "onInvoke"
> & {
  withParams: (params: z.infer<ParamsSchema>) => Action<ParamsSchema>;
};

// export type Action<ParamsSchema extends ZodSchema | undefined = undefined> =
//   ActionConfig<ParamsSchema> & {
//     invoke: (
//       props: Omit<Parameters<Invoke<ParamsSchema>>[0], "params">
//     ) => void;
//   };

export type Action<ParamsSchema extends ZodSchema | undefined = undefined> =
  ActionConfig<ParamsSchema>;

export type ActionRegistryKey = string;

export type ActionReference<ParamsSchema = any> =
  | ActionRegistryKey
  | { type: ActionRegistryKey; params?: ParamsSchema; fieldPath?: string };

export type PreparedAction = {
  action: Action<any>;
  name: string | ReactElement;
  canInvoke: boolean;
  hideOnNotInvokable: boolean;
  tooltip: React.ReactNode;
  records: any[];
  permissions?: ActionPermissionResults;
  params?: Record<string, any>;
};
